/**
 * Clinics and Facilities-related GraphQL queries
 * Clinic management, facilities, providers, and appointments
 */

export const CLINICS_QUERIES = {
  // Clinic queries
  GET_AVAILABLE_CLINICS: `
    query GetAvailableClinics {
      pep_vw_available_clinics {
        clinic_id
        clinic_name
        patient_clinic_timezone
      }
    }
  `,

  // Time Slots Management queries
  GET_AVAILABLE_CLINICS_WITH_ENROLLER: `
    query GetAvailableClinicsWithEnroller {
      pep_vw_available_clinics {
        clinic_id
        clinic_name
        patient_clinic_timezone
        has_active_enroller
      }
    }
  `,

  GET_FACILITIES_BY_CLINIC: `
    query GetFacilitiesByClinic($clinicId: Int!) {
      pep_vw_consolidated_data(
        where: {clinic_id: {_eq: $clinicId}},
        distinct_on: facility_id
      ) {
        facility_id
        facility_name
      }
    }
  `,

  // Facilities by Clinic query
  GET_FACILITIES_BY_CLINIC_ID: `
    query GetFacilitiesByClinicId($clinicId: Int!) {
      pep_vw_consolidated_data(
        where: {clinic_id: {_eq: $clinicId}},
        distinct_on: facility_id
      ) {
        facility_id
        facility_name
      }
    }
  `,

  // Providers by Clinic and Facilities query
  GET_PROVIDERS_BY_CLINIC_AND_FACILITIES: `
    query GetProvidersByClinicAndFacilities($clinicId: Int!, $facilityIds: [Int!]!) {
      pep_vw_consolidated_data(
        where: {
          clinic_id: {_eq: $clinicId},
          facility_id: {_in: $facilityIds}
        },
        distinct_on: provider_id
      ) {
        provider_id
        provider_name
      }
    }
  `,

  // Dropdown Data queries
  GET_PROVIDERS_BY_CLINIC: `
    query GetProvidersByClinic($clinicId: Int!) {
      pep_vw_consolidated_data(where: {clinic_id: {_eq: $clinicId}}, distinct_on: provider_id) {
        provider_id
        provider_name
      }
    }
  `,

  GET_PROVIDERS_BY_CLINIC_DISTINCT: `
    query GetProvidersByClinicDistinct($clinicId: Int!) {
      pep_vw_consolidated_data(
        where: {clinic_id: {_eq: $clinicId}},
        distinct_on: provider_id
      ) {
        provider_id
        provider_name
      }
    }
  `,

  // Appointment Slots queries
  GET_AVAILABLE_CLINICS_BASIC: `
    query GetAvailableClinicsBasic {
      pep_vw_available_clinics {
        clinic_id
        clinic_name
        patient_clinic_timezone
      }
    }
  `,

  GET_CLINIC_APPOINTMENTS: `
    query GetClinicAppointments($limit: Int!, $offset: Int!, $whereClause: String!) {
      pep_vw2_clinic_appointments(
        limit: $limit,
        offset: $offset,
        where: $whereClause,
        order_by: {appointment_date: desc}
      ) {
        id
        clinic_id
        clinic_name
        facility_id
        facility_name
        appointment_date
        appointment_hour
        is_booked
        patient_clinic_timezone
      }
    }
  `,

  // From GRAPHQL_QUERIES - Dropdown Clinic and Providers
  DROPDOWN_AVAILABLE_CLINICS: `
    query MyQuery {
      pep_vw_available_clinics {
        clinic_id
        clinic_name
        patient_clinic_timezone
      }
    }
  `,

  // Dropdown Provider (from GRAPHQL_QUERIES)
  DROPDOWN_PROVIDERS_BY_CLINIC: `
    query MyQuery($clinicName: String) {
      pep_vw_consolidated_data(where: {clinic_id: {_eq: 2}}, distinct_on: provider_id) {
        provider_id
        provider_name
      }
    }
  `,

  // Dropdown Facility (from GRAPHQL_QUERIES)
  DROPDOWN_FACILITIES_BY_CLINIC: `
    query MyQuery($clinicName: String) {
      pep_vw_consolidated_data(where: {clinic_id: {_eq: 2}}, distinct_on: facility_id) {
        facility_id
        facility_name
      }
    }
  `,

  // Dropdown Facility by Provider (from GRAPHQL_QUERIES)
  DROPDOWN_FACILITY_BY_PROVIDER: `
    query MyQuery($clinicName: String) {
      pep_vw_consolidated_data(where: {clinic_id: {_eq: 2}}, distinct_on: provider_id) {
        provider_id
        provider_name
      }
    }
  `,

  // Available Clinics (from GRAPHQL_QUERIES)
  AVAILABLE_CLINICS: `
    query MyQuery {
      pep_vw_available_clinics {
        clinic_id
        clinic_name
        patient_clinic_timezone
      }
    }
  `,

  // Available Facilities by Clinic (from GRAPHQL_QUERIES)
  AVAILABLE_FACILITIES_BY_CLINIC: `
    query MyQuery($clinicIds: [Int!]) {
      pep_vw_consolidated_data(where: {clinic_id: {_in: $clinicIds}}, distinct_on: facility_id) {
        facility_id
        facility_name
      }
    }
  `,

  // Available Providers by Clinic (from GRAPHQL_QUERIES)
  AVAILABLE_PROVIDERS_BY_CLINIC: `
    query MyQuery($clinicIds: [Int!]) {
      pep_vw_consolidated_data(where: {clinic_id: {_in: $clinicIds}}, distinct_on: provider_id) {
        provider_id
        provider_name
      }
    }
  `,

  // Available Providers by Clinic and Facilities selected (from GRAPHQL_QUERIES)
  AVAILABLE_PROVIDERS_BY_CLINIC_AND_FACILITIES: `
    query MyQuery($clinicIds: [Int!], $facilityIds: [Int!]) {
      pep_vw_consolidated_data(where: {clinic_id: {_in: $clinicIds}, facility_id: {_in: $facilityIds}}, distinct_on: provider_id) {
        provider_id
        provider_name
      }
    }
  `,

  // Get Facilities (from GRAPHQL_QUERIES)
  GET_FACILITIES_BY_CLINIC_SIMPLE: `
    query GetFacilities($clinicId: Int!) {
      pep_vw_consolidated_data(where: {clinic_id: {_eq: $clinicId}}, distinct_on: facility_id) {
        facility_id
        facility_name
      }
    }
  `,

  // Get Providers (from GRAPHQL_QUERIES)
  GET_PROVIDERS_BY_CLINIC_SIMPLE: `
    query GetProviders($clinicId: Int!) {
      pep_vw_consolidated_data(where: {clinic_id: {_eq: $clinicId}}, distinct_on: provider_id) {
        provider_id
        provider_name
      }
    }
  `,

  // Get Providers for Facilities (from GRAPHQL_QUERIES)
  GET_PROVIDERS_BY_FACILITIES: `
    query GetProvidersForFacilities($clinicId: Int!, $facilityIds: [Int!]!) {
      pep_vw_consolidated_data(where: {clinic_id: {_eq: $clinicId}, facility_id: {_in: $facilityIds}}, distinct_on: provider_id) {
        provider_id
        provider_name
      }
    }
  `,

  // ES Clinic Numbers (from GRAPHQL_QUERIES)
  CLINIC_NUMBERS: `
    query getClinicNumber($clinicId: Int, $stateId: Int) {
      api_origin_number_master(where: {
        is_deleted: {_eq: false},
        is_active: {_eq: true},
        _or: [
          {
            clinic_mapped: {
              _or: [
                {
                  clinic_id: {_eq: $clinicId},
                  is_deleted: {_eq: false}
                },
                {
                  state_id: {_eq: $stateId},
                  is_deleted: {_eq: false}
                }
              ]
            }
          },
          {is_global: {_eq: true}}
        ],
        origin_number_type_id: {_in: [1, 4]}},
        order_by: {number_source_id: asc})
      {
        is_active
        is_global
        number_source_id
        phone_number
        origin_number_type_id
        clinic_mapped (where: {is_deleted: {_eq: false}}) {
          clinic_id
          state_id
        }
      }
    }
  `,

    // ES Number Source Types (from GRAPHQL_QUERIES)
    CLINIC_NUMBER_SOURCE_TYPES: `
    query getNumberSourceTypes {
      reference_number_source(where: {is_deleted: {_eq: false}}) {
        is_deleted, number_source, number_source_id
      }
    }
  `,

  // Appointment Slots Management queries (from GRAPHQL_QUERIES)
  APPOINTMENT_SLOTS_CLINICS: `
    query MyQuery {
      pep_vw_available_clinics {
        clinic_id
        clinic_name
        patient_clinic_timezone
      }
    }
  `,

  // Appointment Slots Facilities (from GRAPHQL_QUERIES)
  APPOINTMENT_SLOTS_FACILITIES: `
    query GetAppointmentSlotsFacilities($clinicId: Int!) {
      pep_vw_consolidated_data(
        where: {clinic_id: {_eq: $clinicId}},
        distinct_on: facility_id
      ) {
        facility_id
        facility_name
      }
    }
  `,

  // Get clinic info by clinic ID for SMS campaigns
  GET_CLINIC_INFO: `
    query MyQuery($hccId: Int) {
      api_healthcarecenter(where: {id: {_eq: $hccId}}) {
        id
        state_id
        name
      }
    }
  `,

  // Get distinct timezones
  GET_TIMEZONES: `
    query GetTimezones {
      api_healthcarecenter(distinct_on: timezone) {
        timezone
      }
    }
  `,
  // Get all enrollment numbers for outbound dialer
  GET_ALL_ENROLLMENT_NUMBERS: `
    query getAllEnrollmentNumber {
      api_origin_number_master(where: {is_active: {_eq: true}, is_deleted: {_eq: false}, origin_number_type_id: {_eq: 1}}) {
        created_by
        created_datetime
        incoming_call_type
        is_active
        is_deleted
        is_global
        number_source_id
        origin_number_type_id
        phone_number
        updated_by
        updated_datetime
      }
    }
  `,

  // Get active healthcare centers
  GET_ACTIVE_HEALTHCARE_CENTERS: `
    query GetActiveHealthcareCenters {
      api_healthcarecenter(where: {active: {_eq: true}}) {
        id
        active
        description
        name
      }
    }
  `,
  // Get outbound call allowed roles from constants
  GET_OUTBOUND_CALL_ROLES: `
    query getOutboundCallRoles {
      api_constant(where: {field_name: {_eq: "DAIL_PAD_ROLE"}}) {
        id
        field_name
        field_value
      }
    }
  `,

  // Get facilities by clinic for Facility Availability
  GET_EMR_FACILITIES_BY_CLINIC: `
    query GetFacilitiesByClinic($clinicId: Int!) {
      emr_clinic_facility(where: {clinic_id: {_eq: $clinicId}}) {
        clinic_facility_id
        facility_name
      }
    }
  `
};